import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ArrowDown, ArrowUp, ChevronsUpDown, ReceiptText, Search, UserRound } from 'lucide-react';
import { useToast } from '../../Common/context/ToastContext';
import {
  getCpCustomerList,
  getCpManualPayments,
  getCpTerminalList,
} from '../api/cp';

dayjs.extend(utc);

const PAGE_SIZE = 50;
const PAYMENT_LIMIT = 100;
const IS_PHPAY = String(process.env.REACT_APP_BRAND_VARIANT || '').trim().toLowerCase() === 'phpay';

function extractRows(data) {
  const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
  return Array.isArray(rows) ? rows : [];
}

function normalizeTerminalList(data) {
  return extractRows(data).map((row, index) => {
    const id = row.id ?? row.terminalPk ?? row.terminal_pk ?? row.terminal_id ?? index;
    return {
      id,
      value: String(id),
      name: row.name ?? row.terminalName ?? row.terminal_name ?? '',
      loginId: row.terminalId ?? row.loginId ?? row.login_id ?? row.code ?? '',
      status: String(row.status ?? 'ACTIVE').toUpperCase(),
    };
  });
}

function createTerminalDirectory(terminals) {
  const directory = new Map();
  terminals.forEach((terminal) => {
    [terminal.id, terminal.value, terminal.loginId].forEach((key) => {
      if (key !== undefined && key !== null && key !== '') directory.set(String(key), terminal);
    });
  });
  return directory;
}

function normalizeCustomerList(data, terminalDirectory, offset = 0) {
  return extractRows(data).map((row, index) => {
    const terminal = row.terminal ?? row.salesperson ?? row.driver ?? {};
    const driverText = typeof row.driver === 'string' ? row.driver : '';
    const customerId = row.customerId ?? row.customer_id ?? row.pharmacyId ?? row.pharmacy_id ?? row.id ?? '';
    const mappingId = row.mappingId ?? row.mapping_id ?? '';
    const terminalPk = row.terminalPk ?? row.terminal_pk ?? row.terminal_id ?? row.terminalId ?? terminal.id ?? '';
    const assignedTerminal = terminalDirectory.get(String(terminalPk));
    const driverName = row.driverName ?? row.driver_name ?? row.salespersonName ?? row.salesperson_name ?? row.terminalName ?? row.terminal_name ?? terminal.name ?? driverText ?? '';
    const driverLoginId = row.driverLoginId ?? row.driver_login_id ?? row.salespersonLoginId ?? row.salesperson_login_id ?? row.terminalLoginId ?? row.terminal_login_id ?? terminal.terminalId ?? terminal.terminal_id ?? '';
    const code = row.externalCustomerCode ?? row.external_customer_code ?? row.institutionCode ?? row.institution_code ?? row.code ?? '';
    const name = row.name ?? row.customerName ?? row.customer_name ?? row.pharmacyName ?? row.pharmacy_name ?? '';
    return {
      key: String(mappingId || customerId || `${code}-${name}-${offset + index}`),
      customerId,
      mappingId,
      terminalPk: assignedTerminal?.value ?? (terminalPk !== '' ? String(terminalPk) : ''),
      code,
      name,
      representative: row.representativeName ?? row.representative_name ?? row.representative ?? row.owner ?? '',
      businessNo: row.businessNo ?? row.business_no ?? '',
      phone: row.phone ?? row.pharmacyPhone ?? row.pharmacy_phone ?? row.tel ?? '',
      address: row.address ?? row.addr ?? '',
      status: String(row.status ?? 'ACTIVE').toUpperCase(),
      driverName: driverName || assignedTerminal?.name || '',
      driverLoginId: driverLoginId || assignedTerminal?.loginId || '',
    };
  });
}

function normalizePaymentList(data) {
  return extractRows(data)
    .filter((row) => String(row.status ?? '').toUpperCase() !== 'FAILED')
    .map((row, index) => {
      const statusRaw = String(row.status ?? '').toUpperCase();
      const status = statusRaw.includes('CANCEL')
        ? (statusRaw.includes('PENDING') ? '취소대기' : '취소')
        : (statusRaw === 'COMPLETED' || statusRaw.includes('APPROVE') || statusRaw.includes('SUCCESS') ? '승인' : (row.status || '-'));
      return {
        id: row.payment_transaction_id ?? row.paymentTransactionId ?? row.id ?? index,
        date: row.approved_at ?? row.approvedAt ?? row.created_at ?? row.createdAt ?? '',
        approvalNo: row.appr_no ?? row.apprNo ?? row.approval_no ?? row.approvalNumber ?? '',
        cardName: row.card_nm ?? row.cardNm ?? row.cardName ?? '-',
        amount: Number(row.amount ?? row.trdAmt ?? row.trd_amt ?? row.pg_trd_amt ?? 0),
        driverName: row.salesperson_name ?? row.salespersonName ?? row.driver_name ?? row.driverName ?? row.terminal_name ?? row.terminalName ?? '',
        status,
      };
    })
    .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
}

function compareText(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'ko-KR', { numeric: true, sensitivity: 'base' });
}

function formatPhone(value) {
  const numbers = String(value ?? '').replace(/[^0-9]/g, '');
  if (!numbers) return '-';
  if (numbers.startsWith('02')) {
    if (numbers.length <= 9) return numbers.replace(/^(02)(\d{3})(\d{4})$/, '$1-$2-$3');
    return numbers.replace(/^(02)(\d{4})(\d{4})$/, '$1-$2-$3');
  }
  return numbers.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
}

function formatPaymentDate(value) {
  if (!value) return '-';
  const date = dayjs.utc(value);
  return date.isValid() ? date.add(9, 'hour').format('YYYY.MM.DD HH:mm') : String(value);
}

function getDefaultPaymentRange() {
  const today = dayjs().add(9, 'hour');
  return {
    dateFrom: today.startOf('month').format('YYYY-MM-DD'),
    dateTo: today.format('YYYY-MM-DD'),
  };
}

function formatWon(value) {
  return `${Number(value || 0).toLocaleString('ko-KR')}원`;
}

function PharmacyAssignmentManage() {
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [terminals, setTerminals] = useState([]);
  const [terminalsReady, setTerminalsReady] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentRange, setPaymentRange] = useState(getDefaultPaymentRange);
  const [appliedPaymentRange, setAppliedPaymentRange] = useState(getDefaultPaymentRange);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const loadTerminals = async () => {
      try {
        const terminalData = await getCpTerminalList({ page: 1, limit: 500 });
        if (cancelled) return;
        const terminalList = normalizeTerminalList(terminalData).sort((a, b) => (
          compareText(a.name || a.loginId, b.name || b.loginId)
        ));
        setTerminals(terminalList);
      } catch (error) {
        if (!cancelled) {
          setTerminals([]);
          showToast(error?.message || '영업자 목록을 불러오지 못했습니다.', 'error');
        }
      } finally {
        if (!cancelled) setTerminalsReady(true);
      }
    };
    loadTerminals();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!terminalsReady) return undefined;
    let cancelled = false;
    const loadCustomers = async () => {
      setLoading(true);
      try {
        const params = { page, limit: PAGE_SIZE, sortBy, sortOrder };
        if (debouncedQuery) params.q = debouncedQuery;
        const data = await getCpCustomerList(params);
        if (cancelled) return;
        const nextRows = normalizeCustomerList(data, createTerminalDirectory(terminals), (page - 1) * PAGE_SIZE);
        setRows(nextRows);
        setTotal(Number(data?.total ?? data?.count) || nextRows.length);
      } catch (error) {
        if (!cancelled) {
          setRows([]);
          setTotal(0);
          setSelected(null);
          showToast(error?.message || '거래처 목록을 불러오지 못했습니다.', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadCustomers();
    return () => { cancelled = true; };
  }, [debouncedQuery, page, sortBy, sortOrder, terminalsReady, terminals]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    setSelected((previous) => {
      if (previous && rows.some((row) => row.key === previous.key)) {
        return rows.find((row) => row.key === previous.key);
      }
      return rows[0] ?? null;
    });
  }, [rows]);

  useEffect(() => {
    if (!selected?.name) {
      setPayments([]);
      return undefined;
    }
    let cancelled = false;
    const loadPayments = async () => {
      setPaymentLoading(true);
      try {
        const data = await getCpManualPayments({
          page: 1,
          limit: PAYMENT_LIMIT,
          q: selected.name,
          dateFrom: appliedPaymentRange.dateFrom,
          dateTo: appliedPaymentRange.dateTo,
        });
        if (!cancelled) setPayments(normalizePaymentList(data));
      } catch (error) {
        if (!cancelled) {
          setPayments([]);
          showToast(error?.message || '거래처 결제내역을 불러오지 못했습니다.', 'error');
        }
      } finally {
        if (!cancelled) setPaymentLoading(false);
      }
    };
    loadPayments();
    return () => { cancelled = true; };
  }, [selected?.key, appliedPaymentRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePaymentRangeSearch = (event) => {
    event?.preventDefault();
    if (!paymentRange.dateFrom || !paymentRange.dateTo) {
      showToast('조회 시작일과 종료일을 모두 선택해 주세요.', 'error');
      return;
    }
    if (paymentRange.dateFrom > paymentRange.dateTo) {
      showToast('조회 시작일은 종료일보다 늦을 수 없습니다.', 'error');
      return;
    }
    setAppliedPaymentRange({ ...paymentRange });
  };

  const handleSort = (nextSortBy) => {
    setPage(1);
    if (sortBy === nextSortBy) {
      setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(nextSortBy);
    setSortOrder('asc');
  };

  const approvedPayments = payments.filter((payment) => payment.status === '승인');
  const approvedAmount = approvedPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const resultCountLabel = debouncedQuery ? `검색 결과 ${total.toLocaleString('ko-KR')}곳` : `총 ${total.toLocaleString('ko-KR')}곳`;

  return (
    <div className="flex-1 min-h-0 overflow-hidden bg-[#f7f8fa] px-6 py-4">
      <div className="h-full min-h-0 flex flex-col gap-3">
        <header className="shrink-0 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-[20px] font-extrabold text-[#191f28]">거래처 관리</h1>
            <p className="mt-1 text-[13px] text-[#6b7684]">거래처 정보와 담당 영업자, 최근 결제내역을 한눈에 확인합니다.</p>
          </div>
          <span className="text-[13px] font-semibold text-[#6b7684]">{resultCountLabel}</span>
        </header>

        <div className="shrink-0">
          <div className="h-[40px] w-[420px] max-w-full flex items-center gap-2 rounded-lg border border-[#e5e8eb] bg-white px-3 focus-within:border-[var(--brand-primary)]">
            <Search size={17} className="shrink-0 text-[#8b95a1]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="거래처코드, 거래처명, 사업자번호 검색"
              className="min-w-0 flex-1 border-0 bg-transparent text-[14px] text-[#191f28] outline-none"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex gap-4">
          <section className="basis-[66%] min-w-0 overflow-hidden rounded-xl border border-[#e5e8eb] bg-white flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              <table className="w-full table-fixed text-left text-[12px]">
                <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]">
                  <tr className="h-[38px] border-b border-[#e5e8eb]">
                    <th className="w-[10%] px-2" aria-sort={sortBy === 'code' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button
                        type="button"
                        onClick={() => handleSort('code')}
                        className="flex w-full items-center gap-1 text-left font-bold hover:text-[#191f28]"
                      >
                        <span className="truncate">거래처코드</span><SortIndicator active={sortBy === 'code'} order={sortOrder} />
                      </button>
                    </th>
                    <th className="w-[18%] px-2" aria-sort={sortBy === 'name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button
                        type="button"
                        onClick={() => handleSort('name')}
                        className="flex w-full items-center gap-1 text-left font-bold hover:text-[#191f28]"
                      >
                        <span className="truncate">거래처명</span><SortIndicator active={sortBy === 'name'} order={sortOrder} />
                      </button>
                    </th>
                    <th className="w-[8%] px-2">대표자</th>
                    <th className="w-[11%] px-2">사업자번호</th>
                    <th className="w-[10%] px-2">연락처</th>
                    <th className="w-[25%] px-2">주소</th>
                    <th className="w-[12%] px-2">담당 영업자</th>
                    <th className="w-[6%] px-2 text-center">상태</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f3f5]">
                  {loading ? (
                    <tr><td colSpan={8} className="px-4 py-14 text-center text-[#8b95a1]">거래처 목록을 불러오는 중입니다.</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={8} className="px-4 py-14 text-center text-[#8b95a1]">조회된 거래처가 없습니다.</td></tr>
                  ) : rows.map((row) => {
                    const isSelected = selected?.key === row.key;
                    return (
                      <tr
                        key={row.key}
                        tabIndex={0}
                        onClick={() => setSelected(row)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelected(row);
                          }
                        }}
                        className={`h-[44px] cursor-pointer outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--brand-primary)] ${isSelected ? 'bg-[var(--brand-primary-soft)]' : 'hover:bg-[#f9fafb]'}`}
                      >
                        <td className="truncate px-2 font-mono text-[#4e5968]" title={row.code || '-'}>{row.code || '-'}</td>
                        <td className="truncate px-2 font-bold text-[#191f28]" title={row.name || '-'}>{row.name || '-'}</td>
                        <td className="truncate px-2 text-[#4e5968]" title={row.representative || '-'}>{row.representative || '-'}</td>
                        <td className="truncate px-2 text-[#4e5968]" title={row.businessNo || '-'}>{row.businessNo || '-'}</td>
                        <td className="truncate px-2 text-[#4e5968]" title={formatPhone(row.phone)}>{formatPhone(row.phone)}</td>
                        <td className="truncate px-2 text-[#6b7684]" title={row.address || '-'}>{row.address || '-'}</td>
                        <td className="truncate px-2 font-semibold text-[#4e5968]" title={[row.driverName, row.driverLoginId].filter(Boolean).join(' / ') || '미할당'}>
                          {row.driverName || row.driverLoginId || <span className="text-[#adb5bd]">미할당</span>}
                        </td>
                        <td className="px-2 text-center">
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${row.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {row.status === 'ACTIVE' ? '활성' : '비활성'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div className="shrink-0 flex items-center justify-center gap-3 border-t border-[#eef1f4] px-4 py-2.5 text-[12px]">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  className="rounded-lg border border-[#e5e8eb] bg-white px-3 py-1.5 font-bold text-[#4e5968] hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  이전
                </button>
                <span className="min-w-[70px] text-center font-semibold text-[#6b7684]">
                  {page} / {lastPage}
                </span>
                <button
                  type="button"
                  disabled={page >= lastPage || loading}
                  onClick={() => setPage((value) => Math.min(lastPage, value + 1))}
                  className="rounded-lg border border-[#e5e8eb] bg-white px-3 py-1.5 font-bold text-[#4e5968] hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  다음
                </button>
              </div>
            )}
          </section>

          <section className="basis-[34%] min-w-0 overflow-hidden rounded-xl border border-[#e5e8eb] bg-white flex flex-col">
            {!selected ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 text-[#adb5bd]">
                <ReceiptText size={32} strokeWidth={1.7} />
                <p className="text-[14px] font-bold">거래처를 선택하면 결제내역이 표시됩니다.</p>
              </div>
            ) : (
              <>
                <div className="shrink-0 border-b border-[#eef1f4] px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-[12px] font-bold text-[var(--brand-primary)]">
                        <UserRound size={14} /> {selected.driverName || selected.driverLoginId || '미할당'}
                      </div>
                      <h2 className="mt-1 truncate text-[17px] font-extrabold text-[#191f28]" title={selected.name}>{selected.name || '-'}</h2>
                      <p className="mt-0.5 font-mono text-[11px] text-[#8b95a1]">거래처코드 {selected.code || '-'}</p>
                    </div>
                    <span className={`shrink-0 rounded px-2 py-1 text-[10px] font-bold ${selected.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {selected.status === 'ACTIVE' ? '활성' : '비활성'}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <SummaryCard label="최근 결제" value={`${payments.length.toLocaleString('ko-KR')}건`} />
                    <SummaryCard label="승인 건수" value={`${approvedPayments.length.toLocaleString('ko-KR')}건`} />
                    <SummaryCard label="승인 합계" value={formatWon(approvedAmount)} accent />
                  </div>
                </div>

                <div className="shrink-0 border-b border-[#eef1f4] px-4 py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <ReceiptText size={16} className="text-[#4e5968]" />
                      <h3 className="text-[14px] font-extrabold text-[#191f28]">최근 결제내역</h3>
                    </div>
                    <span className="text-[11px] text-[#8b95a1]">최대 {PAYMENT_LIMIT}건</span>
                  </div>
                  <form onSubmit={handlePaymentRangeSearch} className="mt-2 flex min-w-0 items-center gap-1.5">
                    <input
                      type="date"
                      max={getDefaultPaymentRange().dateTo}
                      value={paymentRange.dateFrom}
                      onChange={(event) => setPaymentRange((range) => ({ ...range, dateFrom: event.target.value }))}
                      className="h-8 min-w-0 flex-1 rounded-md border border-[#dfe3e8] bg-white px-1.5 text-[11px] text-[#333d4b] outline-none focus:border-[var(--brand-primary)]"
                      aria-label="결제내역 조회 시작일"
                    />
                    <span className="shrink-0 text-[11px] text-[#8b95a1]">~</span>
                    <input
                      type="date"
                      max={getDefaultPaymentRange().dateTo}
                      value={paymentRange.dateTo}
                      onChange={(event) => setPaymentRange((range) => ({ ...range, dateTo: event.target.value }))}
                      className="h-8 min-w-0 flex-1 rounded-md border border-[#dfe3e8] bg-white px-1.5 text-[11px] text-[#333d4b] outline-none focus:border-[var(--brand-primary)]"
                      aria-label="결제내역 조회 종료일"
                    />
                    <button
                      type="submit"
                      disabled={paymentLoading}
                      className="h-8 shrink-0 rounded-md bg-[var(--brand-primary)] px-2.5 text-[11px] font-bold text-white hover:bg-[var(--brand-primary-hover)] disabled:cursor-wait disabled:opacity-60"
                    >
                      {paymentLoading ? '조회 중' : '조회'}
                    </button>
                  </form>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                  <table className="w-full table-fixed text-[11px]">
                    <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]">
                      <tr className="h-[34px] border-b border-[#eef1f4]">
                        <th className="w-[28%] px-2 text-left">결제일</th>
                        <th className="w-[34%] px-2 text-left">승인번호 · 카드사</th>
                        <th className="w-[24%] px-2 text-right">금액</th>
                        <th className="w-[14%] px-2 text-center">상태</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#f1f3f5]">
                      {paymentLoading ? (
                        <tr><td colSpan={4} className="px-3 py-12 text-center text-[#8b95a1]">결제내역을 불러오는 중입니다.</td></tr>
                      ) : payments.length === 0 ? (
                        <tr><td colSpan={4} className="px-3 py-12 text-center text-[#8b95a1]">이 거래처의 결제내역이 없습니다.</td></tr>
                      ) : payments.map((payment) => {
                        const canceled = String(payment.status).includes('취소');
                        const approvalCard = [payment.approvalNo || '-', payment.cardName || '-'].join(' · ');
                        return (
                          <tr key={payment.id} className={canceled ? 'bg-red-50/70' : 'hover:bg-[#f9fafb]'}>
                            <td className="truncate px-2 py-2 text-[#4e5968]" title={formatPaymentDate(payment.date)}>
                              {formatPaymentDate(payment.date)}
                            </td>
                            <td className="min-w-0 px-2 py-2">
                              <p className="truncate font-semibold text-[#333d4b]" title={approvalCard}>
                                {approvalCard}{!IS_PHPAY && payment.driverName ? ` · ${payment.driverName}` : ''}
                              </p>
                            </td>
                            <td className="truncate px-2 py-2 text-right font-bold tabular-nums text-[#191f28]" title={formatWon(payment.amount)}>{formatWon(payment.amount)}</td>
                            <td className={`truncate px-2 py-2 text-center font-bold ${canceled ? 'text-red-600' : 'text-emerald-700'}`}>{payment.status}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function SortIndicator({ active, order }) {
  if (!active) return <ChevronsUpDown size={13} aria-hidden="true" className="shrink-0 text-[#adb5bd]" />;
  if (order === 'asc') return <ArrowUp size={13} aria-hidden="true" className="shrink-0 text-[var(--brand-primary)]" />;
  return <ArrowDown size={13} aria-hidden="true" className="shrink-0 text-[var(--brand-primary)]" />;
}

function SummaryCard({ label, value, accent = false }) {

  return (
    <div className="min-w-0 rounded-lg bg-[#f7f8fa] px-3 py-2">
      <span className="block text-[10px] font-semibold text-[#8b95a1]">{label}</span>
      <strong className={`mt-0.5 block truncate text-[12px] ${accent ? 'text-[var(--brand-primary)]' : 'text-[#333d4b]'}`} title={value}>{value}</strong>
    </div>
  );
}

export default PharmacyAssignmentManage;
